import {beDateFormat, DateRangeType} from '@fl/cmsch-fe-library';
import moment from 'moment';

const isGreater = (firstValue: string, secondValue: string): boolean =>
    moment(firstValue).toDate().getTime() > moment(secondValue).toDate().getTime();

const isValidBeDate = (value: string): boolean =>
    moment(value, beDateFormat, true).isValid();

/**
 * Validates that both values in a date range use the backend date format.
 *
 * @param value - The date range to validate.
 * @returns A validation message when the format is invalid, otherwise `undefined`.
 */
export const validateFormat = (value: DateRangeType | undefined | null): string | undefined => {
    if (value?.[0] && !isValidBeDate(value[0])) {
        return 'Počáteční datum je chybně vyplněno.';
    }
    if (value?.[1] && !isValidBeDate(value[1])) {
        return 'Koncové datum je chybně vyplněno.';
    }

    return undefined;
};

/**
 * Validates that the start date is not after the end date.
 *
 * @param value - The date range to validate.
 * @returns A validation message when the start date is greater than the end date, otherwise `undefined`.
 */
export const maxIsGreater = (value: DateRangeType | undefined | null): string | undefined =>
    value && (value[0] !== null && value[1] !== null && isGreater(value[0], value[1]))
        ? 'Počáteční datum musí obsahovat dřívější a nebo stejné datum jako koncové datum.'
        : undefined;

/**
 * Creates a validator that rejects end dates after a maximum date.
 *
 * @param max - The maximum allowed date.
 * @returns A validator function for a date range.
 */
export const validateMaxValue = (max: string) =>
    (value: DateRangeType | undefined | null): string | undefined =>
        value?.[1] && isGreater(value[1], max)
            ? `Koncové datum nesmí být vyšší než ${max}`
            : undefined;

/**
 * Creates a validator that rejects start dates before a minimum date.
 *
 * @param min - The minimum allowed date.
 * @returns A validator function for a date range.
 */
export const validateMinValue = (min: string) =>
    (value: DateRangeType | undefined | null): string | undefined =>
        value?.[0] && isGreater(min, value[0])
            ? `Počáteční datum nesmí být nižší než ${min}`
            : undefined;
